import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from '../components/Layout';
import { businessAPI, SOCKET_URL } from '../services/api';
import { io } from 'socket.io-client';
import './Businesses.css';

const STAGE_LABELS = {
  starting: 'Başlatılıyor',
  searching: 'Aranıyor',
  collecting: 'Sonuçlar taranıyor',
  processing: 'Detaylar alınıyor',
  completed: 'Tamamlandı',
  cancelled: 'Durduruldu',
  failed: 'Hata'
};

const SOCIAL_LINKS = [
  { field: 'instagram', label: 'IG', title: 'Instagram' },
  { field: 'facebook', label: 'FB', title: 'Facebook' },
  { field: 'twitter', label: 'X', title: 'X (Twitter)' },
  { field: 'linkedin', label: 'in', title: 'LinkedIn' },
  { field: 'youtube', label: 'YT', title: 'YouTube' },
  { field: 'tiktok', label: 'TT', title: 'TikTok' }
];

const EMPTY_SCRAPE = { city: '', district: '', category: '', maxResults: 50 };

function formatDuration(job, now) {
  if (!job?.startedAt) return '';
  const end = job.finishedAt ? new Date(job.finishedAt).getTime() : now;
  const seconds = Math.max(0, Math.round((end - new Date(job.startedAt).getTime()) / 1000));
  const minutes = Math.floor(seconds / 60);
  return minutes ? `${minutes} dk ${seconds % 60} sn` : `${seconds} sn`;
}

function displayHost(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function Businesses() {
  const [businesses, setBusinesses] = useState([]);
  const [filters, setFilters] = useState({
    city: '',
    district: '',
    category: '',
    q: '',
    has: ''
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0
  });
  const [sorting, setSorting] = useState({
    sortBy: 'createdAt',
    sortOrder: 'DESC'
  });
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showScrapeModal, setShowScrapeModal] = useState(false);
  const [scrapeData, setScrapeData] = useState(EMPTY_SCRAPE);
  const [scrapeDistricts, setScrapeDistricts] = useState([]);
  const [starting, setStarting] = useState(false);
  const [job, setJob] = useState(null);
  const [notice, setNotice] = useState(null);
  const [now, setNow] = useState(Date.now());
  const socketRef = useRef(null);
  const loadBusinessesRef = useRef(null);

  const jobRunning = job?.status === 'running';

  const jobStatusRef = useRef(null);

  const handleJobUpdate = useCallback((data) => {
    if (!data) return;
    // İş bittiği anda listeyi yenile
    if (jobStatusRef.current === 'running' && data.status !== 'running') {
      loadBusinessesRef.current?.(1);
    }
    jobStatusRef.current = data.status;
    setJob(data);
  }, []);

  useEffect(() => {
    loadFilterOptions();
    loadBusinesses(1);

    // Sayfa açıldığında devam eden bir iş varsa ilerlemesini göster
    businessAPI.getScrapeStatus()
      .then((response) => {
        if (response.data.data?.status === 'running') handleJobUpdate(response.data.data);
      })
      .catch(() => {});

    const socket = io(SOCKET_URL, {
      auth: { token: localStorage.getItem('token') }
    });
    socketRef.current = socket;

    socket.on('scrape-progress', handleJobUpdate);
    socket.on('connect_error', (error) => {
      console.warn('Socket bağlantı hatası:', error.message);
    });

    return () => {
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // İş sürerken süreyi güncelle; socket bağlı değilse durumu sorgulayarak takip et
  useEffect(() => {
    if (!jobRunning) return undefined;
    const timer = setInterval(() => {
      setNow(Date.now());
      if (!socketRef.current?.connected) {
        businessAPI.getScrapeStatus()
          .then((response) => handleJobUpdate(response.data.data))
          .catch(() => {});
      }
    }, 3000);
    return () => clearInterval(timer);
  }, [jobRunning, handleJobUpdate]);

  useEffect(() => {
    if (filters.city) {
      loadDistricts(filters.city);
    } else {
      setDistricts([]);
    }
  }, [filters.city]);

  const loadFilterOptions = async () => {
    try {
      const response = await businessAPI.getFilterOptions();
      setCities(response.data.data.cities);
      setCategories(response.data.data.categories);
    } catch (error) {
      console.error('Filtre seçenekleri yüklenemedi:', error);
    }
  };

  const loadDistricts = async (city) => {
    try {
      const response = await businessAPI.getDistricts(city);
      setDistricts(response.data.data);
    } catch (error) {
      console.error('İlçeler yüklenemedi:', error);
    }
  };

  const loadBusinesses = async (page = pagination.currentPage) => {
    setLoading(true);
    try {
      const response = await businessAPI.getAll({
        ...filters,
        page: page || 1,
        limit: 25,
        sortBy: sorting.sortBy,
        sortOrder: sorting.sortOrder
      });

      setBusinesses(response.data.data);
      setPagination({
        currentPage: response.data.currentPage,
        totalPages: response.data.totalPages,
        total: response.data.total
      });
    } catch (error) {
      console.error('İşletmeler yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };
  loadBusinessesRef.current = loadBusinesses;

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value, ...(name === 'city' ? { district: '' } : {}) });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadBusinesses(1);
  };

  const handleSort = (field) => {
    // Aynı alana tekrar tıklanırsa sıralama yönünü değiştir
    let newSortOrder = 'ASC';
    if (sorting.sortBy === field) {
      newSortOrder = sorting.sortOrder === 'ASC' ? 'DESC' : 'ASC';
    }
    setSorting({ sortBy: field, sortOrder: newSortOrder });
  };

  // Sıralama değiştiğinde yeniden yükle (ilk render hariç)
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    loadBusinesses(pagination.currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting.sortBy, sorting.sortOrder]);

  const handleScrapeCityChange = async (city) => {
    setScrapeData({ ...scrapeData, city, district: '' });
    if (city) {
      try {
        const response = await businessAPI.getDistricts(city);
        setScrapeDistricts(response.data.data);
      } catch (error) {
        console.error('İlçeler yüklenemedi:', error);
      }
    } else {
      setScrapeDistricts([]);
    }
  };

  const showError = (error, fallback) => {
    setNotice({ type: 'error', text: error.response?.data?.message || fallback });
    // Başka bir iş çalışıyorsa onun ilerlemesini göster
    if (error.response?.data?.data) handleJobUpdate(error.response.data.data);
  };

  const handleScrape = async (e) => {
    e.preventDefault();
    setStarting(true);
    setNotice(null);
    try {
      const response = await businessAPI.scrape(scrapeData);
      handleJobUpdate(response.data.data);
      setShowScrapeModal(false);
      setScrapeData(EMPTY_SCRAPE);
    } catch (error) {
      showError(error, 'Toplama işlemi başlatılamadı');
      setShowScrapeModal(false);
    } finally {
      setStarting(false);
    }
  };

  const handleRefresh = async () => {
    const scope = [filters.city, filters.district, filters.category].filter(Boolean).join(' / ') || 'tüm kayıtlar';
    const ok = window.confirm(
      `${scope} için ${pagination.total} işletmenin telefon, web sitesi ve sosyal medya bilgileri ` +
      `Google Maps'ten yeniden okunacak. İşletme başına birkaç saniye sürer. Devam edilsin mi?`
    );
    if (!ok) return;
    setNotice(null);
    try {
      const response = await businessAPI.refresh({
        city: filters.city,
        district: filters.district,
        category: filters.category
      });
      handleJobUpdate(response.data.data);
    } catch (error) {
      showError(error, 'Güncelleme başlatılamadı');
    }
  };

  const handleCancel = async () => {
    try {
      const response = await businessAPI.cancelScrape();
      handleJobUpdate(response.data.data);
    } catch (error) {
      showError(error, 'İşlem durdurulamadı');
    }
  };

  const percent = job?.total ? Math.min(100, Math.round((job.current / job.total) * 100)) : 0;

  const sortHeader = (field, label) => (
    <th onClick={() => handleSort(field)} className="sortable">
      {label}
      {sorting.sortBy === field && (
        <span className="sort-icon">{sorting.sortOrder === 'ASC' ? ' ▲' : ' ▼'}</span>
      )}
    </th>
  );

  return (
    <Layout>
      <div className="businesses">
        <div className="page-header">
          <h1>İşletmeler</h1>
          <div className="page-actions">
            <button className="btn-secondary" onClick={handleRefresh} disabled={jobRunning || pagination.total === 0}>
              ↻ Bilgileri Güncelle
            </button>
            <button className="btn-primary" onClick={() => setShowScrapeModal(true)} disabled={jobRunning}>
              + Yeni İşletme Topla
            </button>
          </div>
        </div>

        {notice && (
          <div className={`notice notice-${notice.type}`}>
            <span>{notice.text}</span>
            <button onClick={() => setNotice(null)} aria-label="Kapat">×</button>
          </div>
        )}

        {job && (
          <div className={`job-panel job-${job.status}`}>
            <div className="job-header">
              <div>
                <span className="job-stage">{STAGE_LABELS[job.stage] || job.stage}</span>
                <span className="job-title">
                  {job.type === 'refresh' ? 'Bilgi güncelleme' : 'İşletme toplama'}
                  {job.params?.city && ` · ${[job.params.category, job.params.district, job.params.city].filter(Boolean).join(', ')}`}
                </span>
              </div>
              {jobRunning ? (
                <button className="btn-link" onClick={handleCancel}>Durdur</button>
              ) : (
                <button className="btn-link" onClick={() => setJob(null)}>Kapat</button>
              )}
            </div>

            <div className="job-message">{job.message}</div>

            {(job.stage === 'processing' || !jobRunning) && job.total > 0 && (
              <div className="progress-bar-wrapper">
                <div className="progress-bar-fill" style={{ width: `${percent}%` }}></div>
              </div>
            )}
            {jobRunning && job.stage !== 'processing' && (
              <div className="progress-bar-wrapper">
                <div className="progress-bar-indeterminate"></div>
              </div>
            )}

            <div className="job-stats">
              {job.total > 0 && <span><strong>{job.current}</strong> / {job.total} işlendi</span>}
              {job.type === 'search' && <span><strong>{job.saved}</strong> yeni eklendi</span>}
              {job.type === 'refresh' && <span><strong>{job.updated}</strong> güncellendi</span>}
              {job.found > 0 && <span>{job.found} sonuç bulundu</span>}
              {job.skipped > 0 && <span>{job.skipped} zaten kayıtlı</span>}
              {job.failed > 0 && <span className="stat-warn">{job.failed} okunamadı</span>}
              <span className="job-time">{formatDuration(job, now)}</span>
            </div>
          </div>
        )}

        <div className="filter-section">
          <form onSubmit={handleSearch}>
            <input
              type="search"
              name="q"
              placeholder="İşletme adı ara..."
              value={filters.q}
              onChange={handleFilterChange}
            />

            <select name="city" value={filters.city} onChange={handleFilterChange}>
              <option value="">Tüm Şehirler</option>
              {cities.map((city) => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>

            <select name="district" value={filters.district} onChange={handleFilterChange} disabled={!filters.city}>
              <option value="">Tüm İlçeler</option>
              {districts.map((district) => (
                <option key={district} value={district}>{district}</option>
              ))}
            </select>

            <select name="category" value={filters.category} onChange={handleFilterChange}>
              <option value="">Tüm Kategoriler</option>
              {categories.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>

            <select name="has" value={filters.has} onChange={handleFilterChange}>
              <option value="">Tüm Kayıtlar</option>
              <option value="phone">Telefonu olanlar</option>
              <option value="email">E-postası olanlar</option>
              <option value="website">Web sitesi olanlar</option>
              <option value="social">Sosyal medyası olanlar</option>
            </select>

            <button type="submit">Ara</button>
          </form>
        </div>

        {loading ? (
          <div className="loading">Yükleniyor...</div>
        ) : (
          <div className="businesses-table">
            <table>
              <thead>
                <tr>
                  {sortHeader('name', 'İşletme Adı')}
                  {sortHeader('district', 'Konum')}
                  {sortHeader('category', 'Kategori')}
                  <th>Telefon</th>
                  <th>E-posta</th>
                  <th>Web / Sosyal Medya</th>
                  {sortHeader('rating', 'Puan')}
                </tr>
              </thead>
              <tbody>
                {businesses.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center' }}>
                      İşletme bulunamadı
                    </td>
                  </tr>
                ) : (
                  businesses.map((business) => (
                    <tr key={business.id}>
                      <td>
                        {business.googleMapsUrl ? (
                          <a href={business.googleMapsUrl} target="_blank" rel="noopener noreferrer" className="business-name">
                            {business.name}
                          </a>
                        ) : business.name}
                      </td>
                      <td>
                        {business.district}
                        <div className="muted">{business.city}</div>
                      </td>
                      <td>{business.category}</td>
                      <td className="nowrap">
                        {business.phone ? <a href={`tel:${business.phone}`}>{business.phone}</a> : '-'}
                      </td>
                      <td>
                        {business.email ? <a href={`mailto:${business.email}`}>{business.email}</a> : '-'}
                      </td>
                      <td>
                        <div className="link-list">
                          {business.website && (
                            <a href={business.website} target="_blank" rel="noopener noreferrer" className="web-link" title={business.website}>
                              {displayHost(business.website)}
                            </a>
                          )}
                          {SOCIAL_LINKS.filter((s) => business[s.field]).map((s) => (
                            <a
                              key={s.field}
                              href={business[s.field]}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`social-badge social-${s.field}`}
                              title={`${s.title}: ${business[s.field]}`}
                            >
                              {s.label}
                            </a>
                          ))}
                          {!business.website && !SOCIAL_LINKS.some((s) => business[s.field]) && '-'}
                        </div>
                      </td>
                      <td className="nowrap">
                        {business.rating ? Number(business.rating).toFixed(1) : '-'}
                        {business.reviewCount > 0 && <span className="muted"> ({business.reviewCount})</span>}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="pagination">
                <div className="pagination-info">
                  Toplam {pagination.total} kayıt - Sayfa {pagination.currentPage} / {pagination.totalPages}
                </div>
                <div className="pagination-controls">
                  <button
                    onClick={() => loadBusinesses(1)}
                    disabled={pagination.currentPage === 1}
                    className="pagination-btn"
                  >
                    İlk
                  </button>
                  <button
                    onClick={() => loadBusinesses(pagination.currentPage - 1)}
                    disabled={pagination.currentPage === 1}
                    className="pagination-btn"
                  >
                    Önceki
                  </button>

                  {/* Sayfa numaraları */}
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => loadBusinesses(pageNum)}
                        className={`pagination-btn ${pagination.currentPage === pageNum ? 'active' : ''}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => loadBusinesses(pagination.currentPage + 1)}
                    disabled={pagination.currentPage === pagination.totalPages}
                    className="pagination-btn"
                  >
                    Sonraki
                  </button>
                  <button
                    onClick={() => loadBusinesses(pagination.totalPages)}
                    disabled={pagination.currentPage === pagination.totalPages}
                    className="pagination-btn"
                  >
                    Son
                  </button>
                </div>
              </div>
            )}
            {pagination.totalPages <= 1 && pagination.total > 0 && (
              <div className="pagination">
                <div className="pagination-info">Toplam {pagination.total} kayıt</div>
              </div>
            )}
          </div>
        )}

        {showScrapeModal && (
          <div className="modal">
            <div className="modal-content">
              <h2>İşletme Verisi Topla</h2>
              <p className="modal-hint">
                Google Maps'te arama yapılır; her yeni işletmenin telefonu, web sitesi, e-postası ve
                sosyal medya hesapları toplanır. Daha önce kaydedilmiş işletmeler atlanır.
              </p>
              <form onSubmit={handleScrape}>
                <div className="form-group">
                  <label>Şehir <span style={{ color: 'red' }}>*</span></label>
                  <select
                    value={scrapeData.city}
                    onChange={(e) => handleScrapeCityChange(e.target.value)}
                    required
                  >
                    <option value="">Şehir Seçin</option>
                    {cities.map((city) => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>İlçe (Opsiyonel)</label>
                  <select
                    value={scrapeData.district}
                    onChange={(e) => setScrapeData({ ...scrapeData, district: e.target.value })}
                    disabled={!scrapeData.city}
                  >
                    <option value="">Tüm İlçeler</option>
                    {scrapeDistricts.map((district) => (
                      <option key={district} value={district}>{district}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Sektör (Opsiyonel)</label>
                  <select
                    value={scrapeData.category}
                    onChange={(e) => setScrapeData({ ...scrapeData, category: e.target.value })}
                  >
                    <option value="">Tüm Sektörler</option>
                    {categories.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Eklenecek yeni işletme sayısı (en fazla 100)</label>
                  <input
                    type="number"
                    value={scrapeData.maxResults}
                    onChange={(e) => setScrapeData({ ...scrapeData, maxResults: e.target.value })}
                    min="1"
                    max="100"
                    required
                  />
                </div>

                <div className="modal-actions">
                  <button type="submit" className="btn-primary" disabled={starting}>
                    {starting ? 'Başlatılıyor...' : 'Başlat'}
                  </button>
                  <button type="button" onClick={() => setShowScrapeModal(false)}>İptal</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Businesses;
